import React, { useState, useEffect, useCallback, useMemo } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { TextField } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ResourceLoading } from "../../utils/ResourceLoading";
import {
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ProConfirmation from "../../utils/ProConfirmation";
import { v4 as uuidv4 } from "uuid";
import { TWclassNames } from "../../utils/Div";
import { PMField } from "../../utils/PMField";
import ModernProductSelectorV2 from "../../utils/Selector/ModernProductSelectorV2";
import { useDispatch, useSelector } from "react-redux";
import { getStorages } from "../../../slices/inventory/storage";
import { fetchProduct } from "../../../api/product";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { useTranslation } from "react-i18next";

// ============================================================================
// Helper Functions
// ============================================================================

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((item, index) => ({
    ...item,
    row_order: index,
  }));
};

const formatPriceInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  const cleaned = stringValue.replace(/[^0-9,.]/g, "");
  return cleaned.replace(/\./g, ",");
};

const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  if (stringValue === "") return "";

  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    let decimalPart = parts[1] || "";
    decimalPart = decimalPart.substring(0, 2);
    if (decimalPart.length === 1) decimalPart = decimalPart + "0";
    if (decimalPart.length === 0) decimalPart = "00";
    return `${integerPart},${decimalPart}`;
  }

  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }

  return stringValue;
};

const formatNumericInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value);
  const cleaned = stringValue.replace(/[^\d-]/g, "");
  if (cleaned.startsWith("-")) {
    return cleaned.replace(/-/g, "").replace(/^/, "-");
  }
  return cleaned.replace(/-/g, "");
};

const parseNumericValue = (value) => {
  if (!value || value === "") return 0;
  const stringValue = String(value);
  const normalized = stringValue.replace(/,/g, ".");
  return parseFloat(normalized) || 0;
};

/** Quantity / hours: allow decimals (e.g. 1.5 h) — plain formatNumericInput strips dots. */
const formatDecimalQuantityInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  let s = String(value).replace(/,/g, ".");
  const neg = s.startsWith("-");
  s = neg ? s.slice(1) : s;
  s = s.replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) {
    s =
      s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
  }
  const parts = s.split(".");
  if (parts[1] && parts[1].length > 6) {
    s = `${parts[0]}.${parts[1].slice(0, 6)}`;
  }
  return neg ? `-${s}` : s;
};

const quantityFieldKeyDownAllowed = (e) =>
  /[0-9]/.test(e.key) ||
  [",", ".", "-", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(
    e.key
  );

const calculateRowTotalPurchasePrice = (item) => {
  let total = 0.0;
  if (item && item.purchase_price && item.quantity) {
    const purchasePrice = parseNumericValue(item.purchase_price);
    const quantity = parseNumericValue(item.quantity);
    total = purchasePrice * quantity;
  }
  return total;
};

const calculateRowTotalPrice = (item) => {
  const salesPrice = parseNumericValue(item.sales_price);
  const salesPriceDivided = salesPrice / 100;
  const surcharge = item.surcharge
    ? salesPriceDivided * parseNumericValue(item.surcharge)
    : 0;
  const discount = item.discount
    ? salesPriceDivided * parseNumericValue(item.discount)
    : 0;
  var total = 0;
  if (item && salesPrice && item.quantity) {
    const quantity = parseNumericValue(item.quantity);
    total = (salesPrice + surcharge - discount) * quantity;
  }
  return total;
};

const calculateTotalPrice = (items) => {
  var total = 0;
  if (items.length > 0) {
    items?.forEach((item) => {
      if (!item.delete && item.group_row == false) {
        total += calculateRowTotalPrice(item);
      }
    });
  }
  return total;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: 0,
  margin: `0 0 8px 0`,
  background: isDragging ? "lightgrey" : null,
  ...draggableStyle,
});

const isTruthyFlag = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const productHasWarehouse = (product) => {
  if (!product) return false;
  return isTruthyFlag(product.warehouse_enabled ?? product.warehouse);
};

const formatNumberLabel = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);

  return new Intl.NumberFormat("nb-NO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const formatDateForInput = (value) => {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const stringValue = String(value);
    return stringValue.includes("T") ? stringValue.split("T")[0] : stringValue;
  }

  return parsed.toISOString().split("T")[0];
};

const formatDateLabel = (value) => {
  const inputValue = formatDateForInput(value);
  if (!inputValue) return "Ikke satt";

  const parsed = new Date(inputValue);
  if (Number.isNaN(parsed.getTime())) return inputValue;

  return new Intl.DateTimeFormat("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

const getInventoryStock = (item) =>
  item?.inventory_stock || item?.product?.inventory_stock || null;

const itemUsesWarehouse = (item) =>
  productHasWarehouse(item?.product) ||
  Boolean(
    item?.storage_id ||
      item?.picked ||
      item?.picked_at ||
      getInventoryStock(item)
  );

const getStorageOptions = (item, storages = []) => {
  const options = new Map();
  const inventoryStock = getInventoryStock(item);
  const stockStorages = Array.isArray(inventoryStock?.storages)
    ? inventoryStock.storages
    : [];

  [...storages, ...stockStorages].forEach((storage) => {
    if (storage?.id) {
      options.set(storage.id, storage);
    }
  });

  return Array.from(options.values());
};

const getStorageLabel = (item, storages = [], automaticLabel = "Automatisk lager") => {
  if (!item?.storage_id) return automaticLabel;

  const matchedStorage = getStorageOptions(item, storages).find(
    (storage) => String(storage.id) === String(item.storage_id)
  );

  return (
    matchedStorage?.title ||
    matchedStorage?.displayTitle ||
    matchedStorage?.storage_number ||
    `Lager ${item.storage_id}`
  );
};

/**
 * Effective picked/delivered qty for display.
 * null/undefined/"" = unset → fall back to ordered quantity when picked.
 * New rows init quantity_picked to 0 without meaning "picked zero"; when
 * picked is set and ordered qty is positive, treat bare 0 as unset too.
 * Explicit picked zero is only kept when ordered quantity is also zero.
 */
const effectivePickedQuantity = (item) => {
  const ordered = item?.quantity;
  const raw = item?.quantity_picked;
  if (raw == null || raw === "") {
    return item?.picked ? ordered : raw;
  }
  const pickedNum = parseNumericValue(raw);
  const orderedNum = parseNumericValue(ordered);
  if (item?.picked && pickedNum === 0 && orderedNum > 0) {
    return ordered;
  }
  return raw;
};

const linePickStatusLabel = (item, t) => {
  if (item?.picked) {
    return t("order.items.pick_status.picked_delivered", {
      qty: formatNumberLabel(effectivePickedQuantity(item)),
    });
  }
  return t("order.items.pick_status.not_picked");
};

const getPreferredStorageId = (stockData) => {
  if (!stockData) return null;

  if (stockData.storage_id) {
    return stockData.storage_id;
  }

  if (!Array.isArray(stockData.storages) || stockData.storages.length === 0) {
    return null;
  }

  const sortedStorages = [...stockData.storages].sort((left, right) => {
    const leftAvailable = parseNumericValue(left?.available_quantity);
    const rightAvailable = parseNumericValue(right?.available_quantity);

    if (rightAvailable !== leftAvailable) {
      return rightAvailable - leftAvailable;
    }

    return (
      parseNumericValue(right?.tracked_quantity) -
      parseNumericValue(left?.tracked_quantity)
    );
  });

  return sortedStorages[0]?.id || null;
};

const createEmptyItemRow = ({
  rowOrder,
  parentTempId = null,
  parentGroupId = null,
} = {}) => {
  const tempId = uuidv4();

  return {
    id: null,
    temp_id: tempId,
    title: "",
    description: "",
    purchase_price: 0,
    sales_price: 0,
    quantity: 0,
    quantity_picked: null,
    discount: 0,
    surcharge: 0,
    group_row: false,
    storage_id: null,
    picked: false,
    picked_at: null,
    inventory_stock: null,
    draggableId: `item-${tempId}`,
    row_order: rowOrder ?? 0,
    parent_temp_id: parentTempId,
    parent_group_id: parentGroupId,
  };
};

// ============================================================================
// Memoized Components
// ============================================================================

const MemoizedGroupRow = React.memo(
  ({
    item,
    provided,
    snapshot,
    deleteRow,
    onItemRowChange,
    groupFieldDisabled,
  }) => {
    if (item.delete) return null;

    return (
      <TableRow
        className="bg-gray-100 hover:bg-gray-200 transition-colors"
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        <TableCell>
          <DragHandleIcon className="text-gray-400" />
        </TableCell>
        <TableCell align="left" colSpan={3}>
          <TextField
            label="Title"
            variant="standard"
            name="title"
            sx={{ width: "50%" }}
            value={item.title || ""}
            onChange={(e) => onItemRowChange(e, item)}
            disabled={groupFieldDisabled}
          />
        </TableCell>
        <TableCell align="left" colSpan={6}>
          <TextField
            label="Kommentar"
            variant="standard"
            name="comment"
            fullWidth
            multiline
            rows={2}
            value={item.comment || ""}
            onChange={(e) => onItemRowChange(e, item)}
            disabled={groupFieldDisabled}
          />
        </TableCell>
        <TableCell align="right">
          <TrashIcon
            className="cursor-pointer h-5 w-5 text-red-400 hover:text-red-600 transition-colors"
            onClick={() => deleteRow(item)}
          />
        </TableCell>
      </TableRow>
    );
  }
);
MemoizedGroupRow.displayName = "MemoizedGroupRow";

const MemoizedItemRow = React.memo(
  ({
    item,
    provided,
    snapshot,
    props,
    deleteRow,
    onItemRowChange,
    handleRowProductSelect,
    calculateRowTotalPrice,
    formatter,
    fieldDisabled,
    compact,
    expandedRows,
    toggleRowExpansion,
    rowErrors,
    storages = [],
    handlePriceBlur,
    warehousePickEnabled,
    openInventoryDialog,
  }) => {
    const { t } = useTranslation();
    if (item.delete) return null;

    const hasProduct = !!item?.product?.id;
    const productDisplayName = hasProduct
      ? item.product.displayTitle ||
        item.product.title ||
        `Product ${item.product.item_number || item.product.id}`
      : item?.title || null;

    const hasRowErrors = rowErrors && Object.keys(rowErrors).length > 0;
    const itemHasWarehouse = itemUsesWarehouse(item);
    const showWarehouseForItem = warehousePickEnabled && itemHasWarehouse;
    const inventoryStock = getInventoryStock(item);
    const storageLabel = getStorageLabel(
      item,
      storages,
      t("order.items.storage.automatic")
    );
    const trackedQuantity =
      inventoryStock?.tracked_quantity ?? item?.product?.stock ?? null;
    const availableQuantity =
      inventoryStock?.available_quantity ?? item?.product?.available_quantity ?? null;
    const pickStatus = linePickStatusLabel(item, t);
    const orderedLabel = t("order.items.qty.ordered", {
      qty: formatNumberLabel(item?.quantity),
    });
    const pickedQtyLabel = item?.picked
      ? t("order.items.qty.picked_delivered", {
          qty: formatNumberLabel(effectivePickedQuantity(item)),
        })
      : null;

    return (
      <React.Fragment>
        <TableRow
          hover
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style
          )}
          className={hasRowErrors ? "bg-red-50" : ""}
        >
          {props.drag && (
            <TableCell>
              <DragHandleIcon className="text-gray-400" />
            </TableCell>
          )}

          <TableCell align="left">
            <div
              className={TWclassNames(
                !fieldDisabled
                  ? "cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:border-sky-300 hover:bg-sky-50/60"
                  : "cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-50 p-4 opacity-60"
              )}
              style={{
                borderColor: rowErrors?.product_id
                  ? "#ef4444"
                  : fieldDisabled
                  ? "#e5e7eb"
                  : "#d1d5db",
                backgroundColor: rowErrors?.product_id ? "#fef2f2" : "inherit",
              }}
              onClick={fieldDisabled ? undefined : () => handleRowProductSelect(item)}
            >
              {hasProduct ? (
                <div>
                  <div
                    className={TWclassNames(
                      fieldDisabled
                        ? "text-gray-400"
                        : "font-semibold text-slate-900"
                    )}
                  >
                    {productDisplayName}
                  </div>
                  {item.product?.item_number && (
                    <div className="text-xs text-gray-500 mt-1">
                      Art.nr: {item.product.item_number}
                    </div>
                  )}
                  {item.description && (
                    <div className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                      {item.description}
                    </div>
                  )}
                  {showWarehouseForItem ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openInventoryDialog(item);
                      }}
                      className="mt-3 flex w-full flex-col gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/80 px-3 py-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-slate-700">
                          <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 ring-1 ring-inset ring-emerald-200">
                            Lagerfort
                          </span>
                          <span className="inline-flex items-center gap-2 rounded-full bg-white px-2.5 py-1 ring-1 ring-inset ring-slate-200">
                            <span
                              className={TWclassNames(
                                "h-3.5 w-3.5 rounded-[4px] border",
                                item?.picked
                                  ? "border-emerald-500 bg-emerald-500"
                                  : "border-slate-300 bg-white"
                              )}
                            />
                            {pickStatus}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 ring-1 ring-inset ring-slate-200">
                            {storageLabel}
                          </span>
                          {item?.picked_at && (
                            <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 ring-1 ring-inset ring-slate-200">
                              {formatDateLabel(item.picked_at)}
                            </span>
                          )}
                        </div>

                        <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                          {t("order.items.warehouse_pick.open")}
                        </span>
                      </div>

                      <div className="grid gap-2 text-[11px] text-slate-600 sm:grid-cols-3">
                        <span>
                          {t("order.items.qty.available", {
                            qty: formatNumberLabel(availableQuantity),
                          })}
                        </span>
                        <span>
                          {t("order.items.qty.tracked", {
                            qty: formatNumberLabel(trackedQuantity),
                          })}
                        </span>
                        <span>
                          {orderedLabel}
                          {pickedQtyLabel ? ` · ${pickedQtyLabel}` : ""}
                        </span>
                      </div>
                    </button>
                  ) : hasProduct ? (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Ikke lagerfort produkt.
                    </div>
                  ) : null}
                </div>
              ) : item?.title ? (
                <div>
                  <div
                    className={TWclassNames(
                      fieldDisabled
                        ? "text-gray-400"
                        : "font-semibold text-blue-700"
                    )}
                  >
                    {item.title}
                  </div>
                  {item.description && (
                    <div className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                      {item.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 italic mt-1">
                    Manuell vare
                  </div>
                </div>
              ) : (
                <div className="py-3 text-center text-slate-500">
                  <div className="text-sm font-medium">
                    + Klikk for å velge produkt
                  </div>
                  <div className="mt-1 text-xs text-slate-400">Velg produkt</div>
                </div>
              )}
            </div>

            <TextField
              disabled={fieldDisabled}
              label="Beskrivelse"
              variant="standard"
              sx={{
                mt: "12px",
                "& .MuiInputBase-root": {
                  backgroundColor: rowErrors?.description ? "#fef2f2" : "inherit",
                  border: rowErrors?.description ? "1px solid #ef4444" : "none",
                },
              }}
              value={item?.description || ""}
              fullWidth
              name="description"
              multiline
              rows={2}
              onChange={(e) => onItemRowChange(e, item)}
            />
          </TableCell>

          {props.quantity && (
            <TableCell align="center" sx={{ maxWidth: "90px", minWidth: "90px" }}>
              <div className="flex items-center justify-center space-x-1.5">
                <TextField
                  disabled={fieldDisabled}
                  variant="standard"
                  value={item?.quantity || ""}
                  name="quantity"
                  onChange={(e) => onItemRowChange(e, item)}
                  onKeyDown={(e) => {
                    if (!quantityFieldKeyDownAllowed(e)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    style: { textAlign: "center" },
                    placeholder: "0",
                    inputMode: "decimal",
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: rowErrors?.quantity ? "#fef2f2" : "inherit",
                      border: rowErrors?.quantity ? "1px solid #ef4444" : "none",
                    },
                    width: "50px",
                  }}
                />
                <span className="text-xs text-gray-600 font-medium">
                  {item?.unit?.title || "-"}
                </span>
              </div>
            </TableCell>
          )}

          {props.quantity_picked && (
            <TableCell align="center" sx={{ maxWidth: "90px", minWidth: "90px" }}>
              <div className="flex items-center justify-center space-x-1.5">
                <TextField
                  disabled={fieldDisabled}
                  variant="standard"
                  value={item?.quantity_picked || ""}
                  name="quantity_picked"
                  onChange={(e) => onItemRowChange(e, item)}
                  onKeyDown={(e) => {
                    if (!quantityFieldKeyDownAllowed(e)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    style: { textAlign: "center" },
                    placeholder: "0",
                    inputMode: "decimal",
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textAlign: "center",
                      fontSize: "0.875rem",
                    },
                    width: "50px",
                  }}
                />
                <span className="text-xs text-gray-600 font-medium">
                  {item?.unit?.title || "-"}
                </span>
              </div>
            </TableCell>
          )}

          {props.purchase_price && (
            <TableCell align="left" sx={{ minWidth: "100px" }}>
              <TextField
                variant="standard"
                value={
                  item?.purchase_price ? formatPriceInput(item.purchase_price) : ""
                }
                fullWidth
                name="purchase_price"
                onChange={(e) => onItemRowChange(e, item)}
                onBlur={(e) => handlePriceBlur(e, item)}
                onKeyDown={(e) => {
                  const allowedKeys = /[0-9,.]/;
                  const isNavigationKey = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Enter",
                  ].includes(e.key);

                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                    e.preventDefault();
                  }
                  if (e.key === ".") {
                    e.preventDefault();
                    onItemRowChange(
                      {
                        target: {
                          name: "purchase_price",
                          value: (e.target.value || "") + ",",
                        },
                      },
                      item
                    );
                  }
                }}
                disabled={fieldDisabled}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
              />
            </TableCell>
          )}

          {props.purchase_price && (
            <TableCell align="left" sx={{ minWidth: "100px" }}>
              <span className="text-sm font-medium text-gray-700">
                {formatter.format(calculateRowTotalPurchasePrice(item))}
              </span>
            </TableCell>
          )}

          {props.sales_price && (
            <TableCell align="left" sx={{ minWidth: "100px" }}>
              <TextField
                variant="standard"
                fullWidth
                name="sales_price"
                value={item.sales_price ? formatPriceInput(item.sales_price) : ""}
                onChange={(e) => onItemRowChange(e, item)}
                onBlur={(e) => handlePriceBlur(e, item)}
                onKeyDown={(e) => {
                  const allowedKeys = /[0-9,.]/;
                  const isNavigationKey = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Enter",
                  ].includes(e.key);

                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                    e.preventDefault();
                  }
                  if (e.key === ".") {
                    e.preventDefault();
                    onItemRowChange(
                      {
                        target: {
                          name: "sales_price",
                          value: (e.target.value || "") + ",",
                        },
                      },
                      item
                    );
                  }
                }}
                disabled={fieldDisabled}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
              />
            </TableCell>
          )}

          {props.surcharge && (
            <TableCell align="left" sx={{ minWidth: "80px" }}>
              <TextField
                variant="standard"
                fullWidth
                name="surcharge"
                value={item.surcharge || ""}
                onChange={(e) => onItemRowChange(e, item)}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]/.test(e.key) &&
                    !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
                      e.key
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                disabled={fieldDisabled}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
              />
            </TableCell>
          )}

          {props.discount && (
            <TableCell align="left" sx={{ minWidth: "80px" }}>
              <TextField
                variant="standard"
                fullWidth
                name="discount"
                value={item.discount || ""}
                onChange={(e) => onItemRowChange(e, item)}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]/.test(e.key) &&
                    !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
                      e.key
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                disabled={fieldDisabled}
                sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem" } }}
              />
            </TableCell>
          )}

          {props.sales_price && (
            <TableCell align="left" sx={{ minWidth: "120px" }}>
              <span className="text-sm font-semibold text-gray-900">
                {formatter.format(calculateRowTotalPrice(item))}
              </span>
            </TableCell>
          )}

          {props.quote_line && !compact && (
            <TableCell align="left">
              <PMField
                type="checkbox"
                name="is_quote_line"
                checked={item.is_quote_line || false}
                onChange={(e) =>
                  onItemRowChange(
                    {
                      target: {
                        name: e.target.name,
                        value: e.target.checked,
                      },
                    },
                    item
                  )
                }
              />
            </TableCell>
          )}

          {compact && (
            <TableCell align="center">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleRowExpansion(item.draggableId);
                }}
                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
              >
                {expandedRows.has(item.draggableId) ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
            </TableCell>
          )}

          <TableCell align="right">
            <TrashIcon
              className="cursor-pointer h-5 w-5 text-red-400 hover:text-red-600 transition-colors"
              onClick={() => deleteRow(item)}
            />
          </TableCell>
        </TableRow>

        {compact && expandedRows.has(item.draggableId) && (
          <TableRow>
            <TableCell colSpan={6} style={{ padding: 0 }}>
              <div className="bg-gray-50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  {props.purchase_price && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Innkjøpspris
                      </label>
                      <TextField
                        variant="standard"
                        value={
                          item?.purchase_price
                            ? formatPriceInput(item.purchase_price)
                            : ""
                        }
                        fullWidth
                        name="purchase_price"
                        onChange={(e) => onItemRowChange(e, item)}
                        onBlur={(e) => handlePriceBlur(e, item)}
                        disabled={fieldDisabled}
                      />
                    </div>
                  )}

                  {props.sales_price && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Enhetspris ekskl. mva
                      </label>
                      <TextField
                        variant="standard"
                        fullWidth
                        name="sales_price"
                        value={
                          item.sales_price ? formatPriceInput(item.sales_price) : ""
                        }
                        onChange={(e) => onItemRowChange(e, item)}
                        onBlur={(e) => handlePriceBlur(e, item)}
                        disabled={fieldDisabled}
                      />
                    </div>
                  )}

                  {props.surcharge && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Påslag (%)
                      </label>
                      <TextField
                        variant="standard"
                        fullWidth
                        name="surcharge"
                        value={item.surcharge || ""}
                        onChange={(e) => onItemRowChange(e, item)}
                        disabled={fieldDisabled}
                      />
                    </div>
                  )}

                  {props.discount && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Rabatt (%)
                      </label>
                      <TextField
                        variant="standard"
                        fullWidth
                        name="discount"
                        value={item.discount || ""}
                        onChange={(e) => onItemRowChange(e, item)}
                        disabled={fieldDisabled}
                      />
                    </div>
                  )}

                  {props.quote_line && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Tilbudslinje
                      </label>
                      <PMField
                        type="checkbox"
                        name="is_quote_line"
                        checked={item.is_quote_line || false}
                        onChange={(e) =>
                          onItemRowChange(
                            {
                              target: {
                                name: e.target.name,
                                value: e.target.checked,
                              },
                            },
                            item
                          )
                        }
                      />
                    </div>
                  )}

                  {showWarehouseForItem && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openInventoryDialog(item);
                      }}
                      className="col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50/80 px-3 py-3 text-left text-sm font-medium text-slate-700"
                    >
                      Apne lager og plukk
                    </button>
                  )}
                </div>

                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="font-medium text-gray-700">
                    Beløp innkjøp:{" "}
                    {formatter.format(calculateRowTotalPurchasePrice(item))}
                  </span>
                  <span className="font-medium text-gray-700">
                    Beløp eks. mva:{" "}
                    {formatter.format(calculateRowTotalPrice(item))}
                  </span>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }
);
MemoizedItemRow.displayName = "MemoizedItemRow";

const InventoryDialog = ({
  item,
  storages = [],
  disabled,
  onClose,
  onStorageChange,
  onPickedChange,
  onPickedAtChange,
}) => {
  const { t } = useTranslation();
  if (!item) return null;

  const inventoryStock = getInventoryStock(item);
  const storageOptions = getStorageOptions(item, storages);
  const trackedQuantity =
    inventoryStock?.tracked_quantity ?? item?.product?.stock ?? null;
  const availableQuantity =
    inventoryStock?.available_quantity ?? item?.product?.available_quantity ?? null;
  const automaticLabel = t("order.items.storage.automatic");
  const selectedStorageLabel = getStorageLabel(item, storages, automaticLabel);
  const pickStatus = linePickStatusLabel(item, t);

  return (
    <div
      className="fixed inset-0 z-[1700] flex items-end justify-center bg-slate-950/55 p-3 sm:items-center sm:p-6"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_80px_-35px_rgba(15,23,42,0.55)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_55%,#ecfeff_100%)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Lager og plukk
              </div>
              <h3 className="mt-2 text-xl font-semibold text-slate-900">
                {item?.product?.displayTitle || item?.product?.title || item?.title || "Ordrelinje"}
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Lagerfunksjoner er kun tilgjengelig for lagerforte produkter.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 px-5 py-5 sm:px-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Valgt lager
              </label>
              <select
                value={item?.storage_id || ""}
                onChange={(event) => onStorageChange(event, item)}
                disabled={disabled}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400"
              >
                <option value="">{automaticLabel}</option>
                {storageOptions.map((storage) => (
                  <option key={storage.id} value={storage.id}>
                    {storage.title ||
                      storage.displayTitle ||
                      storage.storage_number ||
                      `Lager ${storage.id}`}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">
                {t("order.items.storage.active", { name: selectedStorageLabel })}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {t("order.items.storage.automatic_help")}
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Plukkestatus
              </label>

              <label className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">Plukket</div>
                  <div className="text-xs text-slate-500">
                    Marker linjen som plukket og sett dato ved behov.
                  </div>
                </div>

                <input
                  type="checkbox"
                  checked={Boolean(item?.picked)}
                  disabled={disabled}
                  onChange={(event) => onPickedChange(event, item)}
                  className="h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
              </label>

              <div className="mt-4">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Dato plukket
                </label>
                <input
                  type="date"
                  value={formatDateForInput(item?.picked_at) || formatDateForInput(new Date())}
                  disabled={disabled || !item?.picked}
                  onChange={(event) => onPickedAtChange(event, item)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Tilgjengelig
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatNumberLabel(availableQuantity)}
                </div>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Lagerfort
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatNumberLabel(trackedQuantity)}
                </div>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  {t("order.items.qty.ordered_label")}
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {formatNumberLabel(item?.quantity)}
                </div>
                {item?.picked && (
                  <div className="mt-2 text-xs font-medium text-emerald-700">
                    {t("order.items.qty.picked_delivered", {
                      qty: formatNumberLabel(effectivePickedQuantity(item)),
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {t("order.items.pick_status.line_label")}
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    {item?.picked
                      ? `${pickStatus} · ${formatDateLabel(item?.picked_at)}`
                      : pickStatus}
                  </div>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
                  {selectedStorageLabel}
                </span>
              </div>
            </div>

            {storageOptions.length > 0 && (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Lageroversikt
                </div>
                <div className="mt-3 space-y-2">
                  {storageOptions.map((storage) => (
                    <div
                      key={storage.id}
                      className="flex items-center justify-between rounded-2xl border border-white/60 bg-white px-4 py-3 text-sm"
                    >
                      <div>
                        <div className="font-medium text-slate-900">
                          {storage.title ||
                            storage.displayTitle ||
                            storage.storage_number ||
                            `Lager ${storage.id}`}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Tilgjengelig {formatNumberLabel(storage.available_quantity)} / Lagerfort{" "}
                          {formatNumberLabel(storage.tracked_quantity)}
                        </div>
                      </div>
                      {String(storage.id) === String(item?.storage_id) && (
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Valgt
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export default function OrderItemsV3(props) {
  const {
    setFieldValue,
    values,
    isLoading,
    name = "items",
    drag,
    disabled,
    compact,
    errors,
  } = props;
  const dispatch = useDispatch();
  const isCompactViewport = useMediaQuery("(max-width:1024px)");

  const fieldDisabled = disabled;
  const groupFieldDisabled = disabled;
  const effectiveCompact = compact || isCompactViewport;
  const warehousePickEnabled = props.showWarehousePick === true;

  const storagesState = useSelector((store) => store.inventory_storages);
  const storages = storagesState?.data || [];

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("nb-NO", {
        style: "currency",
        currency: "NOK",
      }),
    []
  );

  // State
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [productSelectorContext, setProductSelectorContext] = useState(null);
  const [productSelectionDraft, setProductSelectionDraft] = useState([]);
  const [productSelectionDirty, setProductSelectionDirty] = useState(false);
  const [isApplyingProductSelection, setIsApplyingProductSelection] =
    useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [inventoryDialogRowId, setInventoryDialogRowId] = useState(null);

  // Build flat items list from values (groups + ungrouped items)
  const items = useMemo(() => {
    const flatItems = [];
    const { groups = [], [name]: ungrouped = [] } = values;

    // Add grouped items
    groups.forEach((group) => {
      if (!group.delete) {
        flatItems.push({ ...group, group_row: true });
        group.items?.forEach((item) => {
          if (!item.delete) {
            flatItems.push({
              ...item,
              group_row: false,
              parent_group_id: group.id,
            });
          }
        });
      }
    });

    // Add ungrouped items (not in any group)
    ungrouped.forEach((item) => {
      if (!item.delete && !item.order_group?.id) {
        flatItems.push({ ...item, group_row: false });
      }
    });

    // If no items, create default empty row
    if (flatItems.length === 0) {
      return [createEmptyItemRow()];
    }

    // Add draggableId and row_order
    return flatItems.map((item, i) => ({
      ...item,
      row_order: i,
      draggableId: item.draggableId || `item-${item.id || item.temp_id || i}-${i}`,
    }));
  }, [values.groups, values[name], name]);

  // Check if ANY product has warehouse - determines if columns should show
  const showWarehouseFields = useMemo(() => {
    if (!warehousePickEnabled) return false;

    for (const item of items) {
      if (!item.group_row && itemUsesWarehouse(item)) {
        return true;
      }
    }
    return false;
  }, [warehousePickEnabled, items]);

  // Load storages when needed
  useEffect(() => {
    if (showWarehouseFields && storages.length === 0) {
      dispatch(getStorages());
    }
  }, [showWarehouseFields, dispatch, storages.length]);

  // Define columns
  const columns = useMemo(
    () => [
      { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
      { id: "product", label: "Produkt", minWidth: 320, active: true },
      {
        id: "quantity",
        label: "Antall/Enhet",
        width: "90px",
        minWidth: "90px",
        maxWidth: 90,
        active: props.quantity ? true : false,
      },
      {
        id: "quantity_picked",
        label: "Plukket/Enhet",
        width: "90px",
        minWidth: "90px",
        maxWidth: 90,
        active: props.quantity_picked ? true : false,
      },
      {
        id: "purchase_price",
        label: "Innkjøpspris",
        minWidth: 100,
        align: "left",
        active: props.purchase_price && !effectiveCompact ? true : false,
      },
      {
        id: "purchase_price_total",
        label: "Beløp innkjøp",
        minWidth: 100,
        align: "left",
        active: props.purchase_price && !effectiveCompact ? true : false,
      },
      {
        id: "sales_price_ex_vat",
        label: "Enhetspris ekskl. mva",
        minWidth: 100,
        align: "left",
        active: props.sales_price && !effectiveCompact ? true : false,
      },
      {
        id: "surcharge",
        label: "Påslag (%)",
        minWidth: 80,
        align: "left",
        active: props.surcharge && !effectiveCompact ? true : false,
      },
      {
        id: "discount",
        label: "Rabatt (%)",
        minWidth: 80,
        align: "left",
        active: props.discount && !effectiveCompact ? true : false,
      },
      {
        id: "total_price",
        label: "Beløp eks. mva",
        minWidth: 120,
        align: "left",
        active: props.sales_price && !effectiveCompact ? true : false,
      },
      {
        id: "is_quote_line",
        label: "Tilbudslinje",
        minWidth: 50,
        align: "left",
        active: props.quote_line && !effectiveCompact ? true : false,
      },
      {
        id: "expand",
        label: "",
        minWidth: 30,
        maxWidth: 30,
        active: effectiveCompact,
      },
      {
        id: "actions",
        label: "",
        minWidth: 24,
        maxWidth: 24,
        active: true,
      },
    ],
    [
      drag,
      props.quantity,
      props.quantity_picked,
      props.purchase_price,
      props.sales_price,
      props.surcharge,
      props.discount,
      props.quote_line,
      effectiveCompact,
    ]
  );

  const activeColumns = useMemo(
    () => columns.filter((column) => column.active === true),
    [columns]
  );

  // Update form values when items change
  const updateFormValues = useCallback(
    (newItems) => {
      const groups = [];
      const ungrouped = [];
      let currentGroup = null;

      newItems.forEach((item) => {
        if (item.group_row) {
          currentGroup = { ...item, items: [] };
          groups.push(currentGroup);
        } else if (
          currentGroup &&
          (item.parent_temp_id === currentGroup.temp_id ||
            item.parent_group_id === currentGroup.id)
        ) {
          currentGroup.items.push(item);
        } else {
          ungrouped.push(item);
        }
      });

      setFieldValue("groups", groups, false);
      setFieldValue(name, ungrouped, false);
    },
    [setFieldValue, name]
  );

  // Handlers
  const addItemRow = useCallback(
    (type) => {
      const tempId = uuidv4();
      const lastItem = items[items.length - 1];
      const isLastItemGroup = lastItem?.group_row === true;

      const newRow = {
        id: null,
        temp_id: tempId,
        title: "",
        description: "",
        purchase_price: 0,
        sales_price: 0,
        quantity: 0,
        discount: 0,
        surcharge: 0,
        group_row: type === "group",
        draggableId: `item-${tempId}`,
        row_order: items.length,
        parent_temp_id:
          type === "group" ? null : isLastItemGroup ? lastItem.temp_id : null,
      };

      const newItems = [...items, newRow];
      updateFormValues(newItems);
    },
    [items, updateFormValues]
  );

  const handleStorageChange = useCallback(
    (e, item) => {
      const newValue = e.target.value;
      const updatedItems = items.map((i) =>
        i.row_order === item.row_order
          ? { ...i, storage_id: newValue === "" ? null : parseInt(newValue) }
          : i
      );
      updateFormValues(updatedItems);
    },
    [items, updateFormValues]
  );

  const handlePickedChange = useCallback(
    (e, item) => {
      const isPicked =
        e.target.checked === true ||
        e.target.value === true ||
        e.target.value === "true";
      const currentPickedQty = parseNumericValue(item.quantity_picked);
      const orderedQty = parseNumericValue(item.quantity);
      const updates = {
        picked: isPicked,
        picked_at:
          isPicked && !item.picked_at
            ? formatDateForInput(new Date())
            : !isPicked
            ? null
            : item.picked_at,
        // Unset / placeholder 0 → ordered qty when marking picked.
        quantity_picked: isPicked
          ? item.quantity_picked == null ||
            item.quantity_picked === "" ||
            (currentPickedQty === 0 && orderedQty > 0)
            ? item.quantity
            : item.quantity_picked
          : null,
      };

      const updatedItems = items.map((i) =>
        i.row_order === item.row_order ? { ...i, ...updates } : i
      );
      updateFormValues(updatedItems);
    },
    [items, updateFormValues]
  );

  const handlePickedAtChange = useCallback(
    (e, item) => {
      const nextValue = e.target.value || null;
      const updatedItems = items.map((i) =>
        i.row_order === item.row_order
          ? {
              ...i,
              picked: nextValue ? true : i.picked,
              picked_at: nextValue,
            }
          : i
      );
      updateFormValues(updatedItems);
    },
    [items, updateFormValues]
  );

  const handlePriceBlur = useCallback(
    (e, data) => {
      const fieldName = e.target.name;
      let inputValue = e.target.value;

      if (
        (fieldName === "purchase_price" || fieldName === "sales_price") &&
        inputValue &&
        inputValue.trim() !== ""
      ) {
        inputValue = formatPriceWithTwoDecimals(inputValue);
        const updatedItems = items.map((i) =>
          i.row_order === data.row_order ? { ...i, [fieldName]: inputValue } : i
        );
        updateFormValues(updatedItems);
      }
    },
    [items, updateFormValues]
  );

  const onItemRowChange = useCallback(
    (e, data) => {
      if (typeof e === "function") {
        e.preventDefault();
      }

      let inputValue = e.target.value;
      const fieldName = e.target.name;

      if (fieldName === "purchase_price" || fieldName === "sales_price") {
        inputValue = formatPriceInput(inputValue);
      } else if (fieldName === "quantity" || fieldName === "quantity_picked") {
        inputValue = formatDecimalQuantityInput(inputValue);
      } else if (["surcharge", "discount"].includes(fieldName)) {
        inputValue = formatNumericInput(inputValue);
      }

      const updatedItems = items.map((i) =>
        i.row_order === data.row_order ? { ...i, [fieldName]: inputValue } : i
      );
      updateFormValues(updatedItems);

      // Clear validation errors
      if (props.setFieldError && props.errors?.[name]?.[data.row_order]) {
        const rowIndex = data.row_order;
        const currentErrors = props.errors[name] || [];

        if (currentErrors[rowIndex]) {
          const item = updatedItems[rowIndex];
          const hasValidProduct =
            item.product_id && String(item.product_id).trim() !== "";
          const hasValidDescription =
            item.description && String(item.description).trim() !== "";
          const hasValidQuantity = item.quantity && Number(item.quantity) > 0;

          if (hasValidProduct || hasValidDescription) {
            if (currentErrors[rowIndex].product_id) {
              props.setFieldError(
                `${name}.${rowIndex}.product_id`,
                undefined
              );
            }
            if (currentErrors[rowIndex].description) {
              props.setFieldError(
                `${name}.${rowIndex}.description`,
                undefined
              );
            }
          }

          if (hasValidQuantity && currentErrors[rowIndex].quantity) {
            props.setFieldError(`${name}.${rowIndex}.quantity`, undefined);
          }
        }
      }
    },
    [items, name, props, updateFormValues]
  );

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const reorderedItems = reorder(
        items,
        result.source.index,
        result.destination.index
      );

      let currentGroup = null;
      const updatedItems = reorderedItems.map((item, index) => {
        if (item.group_row) {
          currentGroup = item;
          return { ...item, row_order: index, parent_group_id: null };
        } else {
          return {
            ...item,
            row_order: index,
            parent_group_id: currentGroup?.id ?? null,
          };
        }
      });

      updateFormValues(updatedItems);
    },
    [items, updateFormValues]
  );

  const hydrateSelectedProducts = useCallback(
    async (products) => {
      return Promise.all(
        products.map(async (product) => {
          let fullProductData = product;
          let stockData = null;

          try {
            const fields =
              "id,title,displayTitle,item_number,purchase_price,sales_price,unit_id,unit(*),warehouse,warehouse_enabled,stock,available_quantity,short_description,brand,status,product_type,warehouse_status_type_id";
            const productResponse = await fetchProduct(product.id, {
              fields,
            });
            if (productResponse?.id) {
              fullProductData = productResponse;
            }
          } catch (error) {
            console.error("Error fetching full product details:", error);
          }

          if (warehousePickEnabled && productHasWarehouse(fullProductData)) {
            try {
              const stockResponse = await warehouseStockApi.getProductStock(product.id);
              if (stockResponse?.success && stockResponse?.data) {
                stockData = stockResponse.data;
              } else if (stockResponse?.data) {
                stockData = stockResponse.data;
              }
            } catch (error) {
              console.error("Error fetching warehouse stock:", error);
            }
          }

          return {
            ...product,
            product: {
              ...fullProductData,
              inventory_stock: stockData || fullProductData.inventory_stock || null,
              available_quantity:
                stockData?.available_quantity ?? fullProductData.available_quantity,
              stock: stockData?.tracked_quantity ?? fullProductData.stock,
            },
            storage_id: getPreferredStorageId(stockData),
          };
        })
      );
    },
    [warehousePickEnabled]
  );

  const buildItemFromSelection = useCallback(
    (baseItem, selection, rowOrder, overrides = {}) => {
      const hasWarehouse = productHasWarehouse(selection.product);
      const quantity =
        parseNumericValue(baseItem?.quantity) > 0
          ? baseItem.quantity
          : parseNumericValue(selection.quantity) > 0
          ? selection.quantity
          : 1;

      return {
        ...createEmptyItemRow({
          rowOrder,
          parentTempId:
            overrides.parent_temp_id !== undefined
              ? overrides.parent_temp_id
              : baseItem?.parent_temp_id || null,
          parentGroupId:
            overrides.parent_group_id !== undefined
              ? overrides.parent_group_id
              : baseItem?.parent_group_id || null,
        }),
        ...baseItem,
        product: selection.product,
        product_id: selection.product.id,
        unit_id: selection.product.unit?.id || selection.product.unit_id,
        unit: selection.product.unit,
        sales_price: selection.product.sales_price,
        purchase_price: selection.product.purchase_price,
        quantity,
        storage_id:
          warehousePickEnabled && hasWarehouse
            ? baseItem?.storage_id || selection.storage_id || null
            : null,
        inventory_stock: selection.product.inventory_stock || null,
        picked: hasWarehouse ? Boolean(baseItem?.picked) : false,
        picked_at: hasWarehouse ? baseItem?.picked_at || null : null,
      };
    },
    [warehousePickEnabled]
  );

  const applyProductSelection = useCallback(
    async (context, draftProducts) => {
      if (!context || !Array.isArray(draftProducts) || draftProducts.length === 0) {
        return;
      }

      setIsApplyingProductSelection(true);
      try {
        const hydratedProducts = await hydrateSelectedProducts(draftProducts);
        let updatedItems = [...items];

        if (context.mode === "replace" && context.row) {
          const rowIndex = context.row.row_order;
          const replacementRows = hydratedProducts.map((selection, index) =>
            buildItemFromSelection(
              index === 0 ? updatedItems[rowIndex] : null,
              selection,
              rowIndex + index,
              {
                parent_temp_id: context.row.parent_temp_id || null,
                parent_group_id: context.row.parent_group_id || null,
              }
            )
          );

          updatedItems = [
            ...updatedItems.slice(0, rowIndex),
            ...replacementRows,
            ...updatedItems.slice(rowIndex + 1),
          ];
        } else if (context.mode === "insert") {
          const insertIndex = context.insertIndex ?? updatedItems.length;
          const insertedRows = hydratedProducts.map((selection, index) =>
            buildItemFromSelection(null, selection, insertIndex + index, {
              parent_temp_id: context.parent_temp_id || null,
              parent_group_id: context.parent_group_id || null,
            })
          );

          updatedItems = [
            ...updatedItems.slice(0, insertIndex),
            ...insertedRows,
            ...updatedItems.slice(insertIndex),
          ];
        }

        updateFormValues(updatedItems);
      } finally {
        setIsApplyingProductSelection(false);
      }
    },
    [buildItemFromSelection, hydrateSelectedProducts, items, updateFormValues]
  );

  const handleRowProductSelect = useCallback((row) => {
    setProductSelectorContext({
      mode: "replace",
      row,
      allowMultiple: !row.id,
      insertIndex: row.row_order,
      selectedProducts: row.product ? [{ ...row.product, quantity: 1 }] : [],
    });
    setProductSelectionDraft(row.product ? [{ ...row.product, quantity: 1 }] : []);
    setProductSelectionDirty(false);
    setProductSelectorOpen(true);
  }, []);

  const handleAddMultipleProducts = useCallback(() => {
    const lastItem = items[items.length - 1];
    setProductSelectorContext({
      mode: "insert",
      allowMultiple: true,
      insertIndex: items.length,
      parent_temp_id: lastItem?.group_row ? lastItem.temp_id : null,
      parent_group_id: lastItem?.group_row ? lastItem.id || null : null,
      selectedProducts: [],
    });
    setProductSelectionDraft([]);
    setProductSelectionDirty(false);
    setProductSelectorOpen(true);
  }, [items]);

  const handleProductDraftChange = useCallback((products) => {
    setProductSelectionDirty(true);
    setProductSelectionDraft(products || []);
  }, []);

  const closeProductSelector = useCallback(
    (applyChanges = true) => {
      const context = productSelectorContext;
      const draftProducts = productSelectionDraft;
      const shouldApply = applyChanges && productSelectionDirty;

      setProductSelectorOpen(false);
      setProductSelectorContext(null);
      setProductSelectionDraft([]);
      setProductSelectionDirty(false);

      if (shouldApply && context && draftProducts.length > 0) {
        applyProductSelection(context, draftProducts);
      }
    },
    [
      applyProductSelection,
      productSelectionDirty,
      productSelectionDraft,
      productSelectorContext,
    ]
  );

  const openInventoryDialog = useCallback((item) => {
    setInventoryDialogRowId(item?.draggableId || null);
  }, []);

  const closeInventoryDialog = useCallback(() => {
    setInventoryDialogRowId(null);
  }, []);

  const toggleRowExpansion = useCallback((rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handleDeleteRow = useCallback(() => {
    setDeleteConfirmationOpen(false);

    if (selectedItemRow) {
      const updatedItems = items.map((item) => {
        if (
          item.id === selectedItemRow.id ||
          item.draggableId === selectedItemRow.draggableId
        ) {
          if (item.group_row) {
            return {
              ...item,
              delete: true,
            };
          }
          return { ...item, delete: true };
        }

        if (item.parent_group_id === selectedItemRow.id) {
          return { ...item, delete: true };
        }

        return item;
      }).map((item, i) => ({
        ...item,
        row_order: i,
        draggableId: `item-${item.id || i}`,
      }));

      updateFormValues(updatedItems);
      setSelectedItemRow(null);
    }
  }, [selectedItemRow, items, updateFormValues]);

  const deleteRow = useCallback((item) => {
    setSelectedItemRow(item);
    setDeleteConfirmationOpen(true);
  }, []);

  if (isLoading) {
    return <ResourceLoading />;
  }

  const visibleItems = items
    .filter((item) => !item.delete)
    .sort((a, b) => (a.row_order ?? 0) - (b.row_order ?? 0));
  const inventoryDialogItem =
    visibleItems.find((item) => item.draggableId === inventoryDialogRowId) || null;
  const totalLineCount = visibleItems.filter((item) => !item.group_row).length;
  const warehouseLineCount = visibleItems.filter(
    (item) => !item.group_row && itemUsesWarehouse(item)
  ).length;

  return (
    <React.Fragment>
      <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_65%,#eef2ff_100%)] px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                  Ordrelinjer
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
                  {totalLineCount} linjer
                </span>
                {showWarehouseFields && (
                  <span className="rounded-full bg-sky-50 px-3 py-1 text-sm font-medium text-sky-700">
                    {warehouseLineCount} lagerlinjer
                  </span>
                )}
              </div>
              <p className="mt-3 max-w-3xl text-sm text-slate-600">
                Velg flere produkter samtidig på nye rader, og bruk dra-håndtaket
                for å endre rekkefølgen. På eksisterende ordrelinjer kan produktet
                byttes én og én.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleAddMultipleProducts}
                className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                Legg til produkter
              </button>
              <button
                type="button"
                onClick={() => addItemRow()}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              >
                Ny tom rad
              </button>
              {props.group && (
                <button
                  type="button"
                  onClick={() => addItemRow("group")}
                  className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Ny gruppering
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto px-2 py-2 sm:px-4">
      <Table sx={{ width: "100%", minWidth: effectiveCompact ? 0 : 1320 }}>
        <TableHead>
          <TableRow>
            {activeColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.id === "actions" ? "right" : column.align}
                style={{
                  top: 57,
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth,
                  width: column.width,
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  textTransform: column.label ? "uppercase" : "none",
                  letterSpacing: column.label ? "0.08em" : 0,
                  color: "#475569",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {visibleItems.map((item, index) => {
                    const uniqueId = item.draggableId || `item-${item.id || item.temp_id || index}-${index}`;
                    return (
                      <Draggable
                        key={uniqueId}
                        draggableId={uniqueId}
                        index={index}
                      >
                        {(provided, snapshot) =>
                          item.group_row === true ? (
                            props.group ? (
                              <MemoizedGroupRow
                                item={item}
                                provided={provided}
                                snapshot={snapshot}
                                deleteRow={deleteRow}
                                onItemRowChange={onItemRowChange}
                                groupFieldDisabled={groupFieldDisabled}
                              />
                            ) : null
                          ) : (
                            <MemoizedItemRow
                              item={item}
                              provided={provided}
                              snapshot={snapshot}
                              props={props}
                              deleteRow={deleteRow}
                              onItemRowChange={onItemRowChange}
                              handleRowProductSelect={handleRowProductSelect}
                              calculateRowTotalPrice={calculateRowTotalPrice}
                              formatter={formatter}
                              fieldDisabled={fieldDisabled}
                              expandedRows={expandedRows}
                              toggleRowExpansion={toggleRowExpansion}
                              rowErrors={errors?.[name]?.[index]}
                              storages={storages}
                              handlePriceBlur={handlePriceBlur}
                              warehousePickEnabled={warehousePickEnabled}
                              openInventoryDialog={openInventoryDialog}
                              compact={effectiveCompact}
                            />
                          )
                        }
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        {errors?.[name] &&
          errors[name].some(
            (itemErrors) => itemErrors && Object.keys(itemErrors).length > 0
          ) && (
            <TableRow>
              <TableCell colSpan={activeColumns.length} sx={{ padding: 0 }}>
                <div className="bg-red-50 border border-red-200 rounded-md p-4 m-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Radfeil
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        {errors[name].map((itemErrors, index) => {
                          if (
                            !itemErrors ||
                            Object.keys(itemErrors).length === 0
                          )
                            return null;
                          return Object.keys(itemErrors).map((fieldName) => {
                            const fieldError = itemErrors[fieldName];
                            if (fieldError) {
                              return (
                                <p
                                  key={`row-${index}-${fieldName}`}
                                  className="mb-1"
                                >
                                  • Rad {index + 1}: {fieldError}
                                </p>
                              );
                            }
                            return null;
                          });
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}

        <TableFooter>
          <TableRow>
            <TableCell align="left" colSpan={activeColumns.length}>
              <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Totalt ekskl. mva
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">
                    {formatter.format(calculateTotalPrice(visibleItems))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <span className="rounded-full bg-white px-3 py-1">
                    Mobilvennlig visning: {effectiveCompact ? "Ja" : "Nei"}
                  </span>
                  {showWarehouseFields && (
                    <span className="rounded-full bg-white px-3 py-1">
                      Lager og plukk er aktivt på denne ordresiden
                    </span>
                  )}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
        </div>
      </div>

      <ModernProductSelectorV2
        open={productSelectorOpen}
        onChange={handleProductDraftChange}
        onClose={() =>
          closeProductSelector(productSelectorContext?.allowMultiple === false)
        }
        selectedProducts={productSelectionDraft}
        multiple={productSelectorContext?.allowMultiple !== false}
        showQuantityInput={productSelectorContext?.allowMultiple !== false}
        onConfirmSelection={
          productSelectorContext?.allowMultiple === false
            ? undefined
            : () => closeProductSelector(true)
        }
        confirmSelectionLabel="Legg til valgte produkter"
        pmdialogDense={true}
      />

      <InventoryDialog
        item={inventoryDialogItem}
        storages={storages}
        disabled={fieldDisabled}
        onClose={closeInventoryDialog}
        onStorageChange={handleStorageChange}
        onPickedChange={handlePickedChange}
        onPickedAtChange={handlePickedAtChange}
      />

      {isApplyingProductSelection && (
        <div className="fixed inset-x-4 bottom-4 z-[1600] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-lg sm:left-auto sm:right-4 sm:w-[320px]">
          Oppdaterer ordrelinjer og henter lagerdata...
        </div>
      )}

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message="Denne ordrelinjen blir slettet permanent."
        action="delete"
        module="order_item"
        yesCallback={handleDeleteRow}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          setSelectedItemRow(null);
        }}
      />
    </React.Fragment>
  );
}

OrderItemsV3.defaultProps = {
  group: false,
  purchase_price: false,
  sales_price: false,
  name: "items",
  drag: true,
  quantity_picked: false,
  quantity: true,
  showWarehousePick: false,
};

